import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import * as Yup from "yup"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import TextInput from "Components/molecule/inputs/text-input"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import { PlusIcon, PencilIcon, TrashIcon } from "Components/atoms/icons"
import { ProjectMarginTarget } from "models/project-margin-target"
import {
    AdminProjectMarginTargetsSliceState, addTarget, removeTarget, setEditingTarget, setTarget, setTargets,
} from "slices/admin/project-margin-targets/reducer"
import {
    deleteProjectMarginTargetRequest, getProjectMarginTargets, submitCreateProjectMarginTarget, submitEditProjectMarginTarget,
} from "slices/admin/project-margin-targets/thunk"
import { clearEffectiveMarginTargets } from "slices/project-managment/margin-targets/reducer"
import FiscalCalendar from "utils/fiscal-calendar"
import { formatDateTimeAmPm } from "utils/tools"

const selectTargets = createSelector(
    (state: any) => state.AdminProjectMarginTargets,
    ({ targets, editingTarget }: AdminProjectMarginTargetsSliceState) => ({ targets, editingTarget })
)

interface Fields {
    fiscalYear: number | ''
    minimumMargin: number | ''
    warningMargin: number | ''
    idealMargin: number | ''
}

const EMPTY_FORM: Fields = { fiscalYear: '', minimumMargin: '', warningMargin: '', idealMargin: '' }

const pct = Yup.number().typeError("Must be a number").required("Required").min(0, "Must be 0 or more").max(100, "Must be 100 or less")

const Validation = Yup.object({
    fiscalYear: Yup.number().typeError("Must be a number").required("Fiscal year is required").integer("Whole number")
        .min(2000, "Fiscal year looks too early").max(2100, "Fiscal year looks too late"),
    minimumMargin: pct,
    warningMargin: pct.test("below-minimum", "Must be lower than the minimum margin",
        function (value) { return value === undefined || this.parent.minimumMargin === undefined || value < this.parent.minimumMargin }),
    idealMargin: pct,
})

const fmtPct = (v: number) => `${v.toLocaleString('en-CA', { maximumFractionDigits: 2 })}%`

const ProjectMarginTargetsPage = () => {
    document.title = "Project Margin Targets | IbexIQ";
    const dispatch: any = useDispatch()
    const { targets, editingTarget } = useSelector(selectTargets)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [focusedTarget, setFocusedTarget] = useState<ProjectMarginTarget | null>(null)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)

    useEffect(() => {
        const init = async () => {
            const res = await getProjectMarginTargets()
            if (res) dispatch(setTargets(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        dispatch(setEditingTarget(null))
        resetForm()
    }

    const onSubmit = async (vals: Fields, helpers: FormikHelpers<Fields>) => {
        helpers.setSubmitting(true)
        const payload = {
            fiscalYear: Number(vals.fiscalYear),
            minimumMargin: Number(vals.minimumMargin),
            warningMargin: Number(vals.warningMargin),
            idealMargin: Number(vals.idealMargin),
        }

        const res = editingTarget
            ? await submitEditProjectMarginTarget(editingTarget.id, payload)
            : await submitCreateProjectMarginTarget(payload)

        if (res) {
            dispatch(editingTarget ? setTarget(res) : addTarget(res))
            // PM screens cache the bands per fiscal year; drop the cache so they reload
            dispatch(clearEffectiveMarginTargets())
            handleCloseFormModal()
        }
        helpers.setSubmitting(false)
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setValues, resetForm, isSubmitting, isValid } =
        useFormik<Fields>({ initialValues: EMPTY_FORM, onSubmit, validationSchema: Validation })

    const handleAdd = () => {
        dispatch(setEditingTarget(null))
        setValues({ ...EMPTY_FORM, fiscalYear: FiscalCalendar.currentFiscalYear() }, false)
        setShowFormModal(true)
    }

    const handleEdit = (target: ProjectMarginTarget) => {
        dispatch(setEditingTarget(target))
        setValues({
            fiscalYear: target.fiscalYear,
            minimumMargin: target.minimumMargin,
            warningMargin: target.warningMargin,
            idealMargin: target.idealMargin,
        }, false)
        setShowFormModal(true)
    }

    const handleCloseDeleteModal = async () => setFocusedTarget(null)

    const confirmDelete = async () => {
        if (!focusedTarget) return
        setIsDeleting(true)
        if (await deleteProjectMarginTargetRequest(focusedTarget.id)) {
            dispatch(removeTarget(focusedTarget.id))
            dispatch(clearEffectiveMarginTargets())
            setFocusedTarget(null)
        }
        setIsDeleting(false)
    }

    const columns = React.useMemo<ColumnDef<ProjectMarginTarget>[]>(() => [
        { header: "Fiscal Year", accessorKey: "fiscalYear", size: 110 },
        {
            header: "Minimum (Good ≥)",
            accessorKey: "minimumMargin",
            size: 150,
            cell: ({ row }) => <strong className="success-dark">{fmtPct(row.original.minimumMargin)}</strong>,
        },
        {
            header: "Warning from",
            accessorKey: "warningMargin",
            size: 140,
            cell: ({ row }) => <span className="warning">{fmtPct(row.original.warningMargin)}</span>,
        },
        {
            header: "Ideal (target)",
            accessorKey: "idealMargin",
            size: 130,
            cell: ({ row }) => fmtPct(row.original.idealMargin),
        },
        {
            header: "Updated",
            id: "updated",
            size: 260,
            accessorFn: (r) => r.updatedAt ? new Date(r.updatedAt).getTime() : undefined,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                <span>
                    {row.original.updatedBy ?? "System (migration)"}
                    <br /><small className="text-muted">{formatDateTimeAmPm(row.original.updatedAt)}</small>
                </span>
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEdit(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => setFocusedTarget(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        },
    ], [])

    const table = useDataTable({
        columns,
        data: targets,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    const field = (name: keyof Fields, label: string, placeholder: string) => (
        <TextInput
            type="number"
            name={name}
            key={name}
            label={label}
            placeholder={placeholder}
            touched={!!touched[name]}
            value={values[name]}
            onBlur={handleBlur}
            onChange={handleChange}
            error={!!touched[name] && !!errors[name]}
            message={touched[name] ? errors[name] : undefined}
        />
    )

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Project Margin Targets"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={[
            <ActionButton key="add" onClick={handleAdd}>Add <PlusIcon /></ActionButton>
        ]}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title={editingTarget ? `Edit FY${editingTarget.fiscalYear} Margin Target` : "Create Margin Target"}
            >
                <div className="generate-documents-form">
                    {field("fiscalYear", "Fiscal Year*", "e.g. 2027")}
                    {field("minimumMargin", "Minimum margin, Good at or above (%)*", "e.g. 45")}
                    {field("warningMargin", "Warning from (%)*", "e.g. 35")}
                    {field("idealMargin", "Ideal / target margin (%)*", "e.g. 45")}
                    <PrimaryButton
                        type='submit'
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !isValid}
                        className="w-100"
                    >
                        {isSubmitting ? "Saving..." : editingTarget ? "Update" : "Create"}
                    </PrimaryButton>
                </div>
            </Modal>
            <AreYouSureModal
                isOpen={!!focusedTarget}
                onModalClose={handleCloseDeleteModal}
                modalTitle="Delete Margin Target"
                subTitle={`Delete the FY${focusedTarget?.fiscalYear ?? ""} target? That year will use the default bands.`}
                handleAreYouSureAccept={confirmDelete}
                handleAreYouSureDecline={handleCloseDeleteModal}
                areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default ProjectMarginTargetsPage
